// ProposalEditPage.jsx
import { useCallback, useEffect, useState } from 'react';
import axiosDefault from 'axios';
import {
  Stack,
  Text,
  TextField,
  PrimaryButton,
  DefaultButton,
  Dropdown,
  Toggle,
  DatePicker,
  Spinner,
  MessageBar,
  MessageBarType,
  Separator,
  Pivot,
  PivotItem,
  ComboBox
} from '@fluentui/react';

// If you already have these, just import your real components:
import TagManager from '../components/TagManager';          // <TagManager entityType="proposals" entityId={id} />
import PeopleManager from '../components/PeopleManager';    // <PeopleManager entityType="proposals" entityId={id} />
import ProposalBioVariationsEditor from '../components/ProposalBioVariationsEditor';
import ProjectLinkManager from "../components/ProjectLinkManager";
import { styles } from './ProposalDashboard';

const ymdToDate = (s) => (s ? new Date(`${s}`) : null);

export default function ProposalEditPage(props) {
  const { proposalId } = props.match.params;
  const axios = axiosDefault;

  // ----- core state -----
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [info, setInfo]       = useState(null);
  const [dirty, setDirty]     = useState(false);

  // Proposal model
  const [model, setModel] = useState({
    title: '',
    client_id: null,
    date_submitted: null,     // YYYY-MM-DD
    status: '',
    summary: '',
    proposal_type: '',
    was_successful: false,
    year: '',
    about: '',
    approach: '',
    workplan: '',
    budget: '',
    search_text: '',
    bios: ''
  });

  // Clients (combobox suggest)
  const [clientOptions, setClientOptions] = useState([]);

  // Team (read-only list; managed by PeopleManager)
  const [team, setTeam] = useState([]); // [{id, first_name, last_name, title, email, ...}]

  // Bios per person: { [person_id]: { options: [{id, title, body, ...}], selectedId: string|null } }
  const [biosByPerson, setBiosByPerson] = useState({});

  const loadProposal = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`/api/proposals/${proposalId}`);
      const clients = await axios.get('/api/clients');
      setClientOptions(clients.data.data);
      const p = Array.isArray(data) ? data[0] : data;
      setModel({
        about: p.about || '',
        approach: p.approach || '',
        budget: p.budget || '',
        canonical_summary: p.canonical_summary || '',
        client_id: p.client_id || null,
        proposal_type: p.proposal_type || '',
        status: p.status || '',
        summary: p.summary || '',
        team: p.team || '',
        title: p.title || '',
        was_successful: p.was_successful ? true : false,
        workplan: p.workplan || '',
        year: p.year || null,
        date_submitted: p.date_submitted || null
      });
      setDirty(false);
    } catch (e) {
      setError(`Failed to load proposal: ${e?.response?.data?.message || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [axios, proposalId]);

  const loadTeam = useCallback(async () => {
    try {
      const { data } = await axios.get(`/api/proposals/${proposalId}/members`);
      setTeam(Array.isArray(data) ? data : []);
    } catch (e) {
      // non-fatal
    }
  }, [axios, proposalId]);

  // Load bios for a person
  const loadBiosForPerson = useCallback(async (personId) => {
    try {
      const { data } = await axios.get(`/api/people/${personId}/bio_variations`, {
        params: { proposal_id: proposalId },
      });
      const options = (Array.isArray(data) ? data : data?.bios || []).map((b) => ({
        key: b.id,
        text: b.title || (b.version_label || `Bio ${b.id}`),
        _raw: b,
      }));
      setBiosByPerson((prev) => ({
        ...prev,
        [personId]: {
          options,
          selectedId: prev[personId]?.selectedId || null, // keep selection if any
        },
      }));
    } catch {
      // swallow; leave empty for that person
      setBiosByPerson((prev) => ({ ...prev, [personId]: { options: [], selectedId: null } }));
    }
  }, [axios, proposalId]);

  // Refresh bios when team changes (fetch for any person lacking options)
  useEffect(() => {
    team.forEach((p) => {
      if (!biosByPerson[p.id]?.options) {
        loadBiosForPerson(p.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team]);

  // Initial loads
  useEffect(() => {
    loadProposal();
    loadTeam();
  }, [loadProposal, loadTeam]);

  // ----- helpers -----
  const setField = (key, val) => {
    setModel((m) => ({ ...m, [key]: val }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      // Compile selected bio IDs to send with the proposal
      const selectedBioIds = Object.entries(biosByPerson)
        .map(([pid, v]) => v?.selectedId)
        .filter(Boolean);

      const payload = {
        ...model
      };

      // console.log(payload);

      await axios.put(`/api/proposals/${proposalId}`, payload);
      setInfo('Saved.');
      setDirty(false);
      // Optionally refresh
      await loadProposal();
    } catch (e) {
      setError(`Failed to save: ${e?.response?.data?.message || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ----- render -----
  if (loading) {
    return (
      <Stack verticalAlign="center" horizontalAlign="center" styles={{ root: { height: '60vh' } }}>
        <Spinner label="Loading proposal…" />
      </Stack>
    );
  };


  // console.log(model);

  return (
    <Stack tokens={{ childrenGap: 12 }} styles={{ root: { maxWidth: 1100, margin: '24px auto', paddingBottom: 24 } }}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Stack>
          <Text variant="xxLarge">Edit Proposal</Text>
          <Text variant="small" styles={{ root: { opacity: 0.7 } }}>
            ID: {proposalId}
          </Text>
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <DefaultButton
            text="Close"
            iconProps={{ iconName: 'Cancel' }}
            onClick={() => props.history.push('/proposals')}
          />
          <PrimaryButton
            text={saving ? 'Saving…' : 'Save'}
            iconProps={{ iconName: 'Save' }}
            disabled={saving || !dirty}
            onClick={save}
          />
        </Stack>
      </Stack>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}
      {info && (
        <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setInfo(null)}>
          {info}
        </MessageBar>
      )}

      <Separator />

      <Pivot styles={{
        // link: {borderRadius: 8},
        text: {fontSize: 16, fontWeight: 400}}}>
        {/* -------- Details tab -------- */}
        <PivotItem headerText="Details">
          <Stack tokens={{ childrenGap: 12 }} className={styles.sectionCard} styles={{ root: { marginTop: 12 } }}>
            <TextField
              label="Title"
              value={model.title}
              onChange={(_, v) => setField('title', v || '')}
              required
            />
            <TextField
              label="Summary"
              value={model.summary}
              onChange={(_, v) => setField('summary', v || '')}
              multiline
              rows={4}
            />
            <Stack horizontal tokens={{ childrenGap: 16 }}>
              <Stack.Item grow={1}>                
                <ComboBox
                  label="Client"
                  placeholder="Type to search clients…"
                  // allowFreeform={false}
                  // autoComplete="on"
                  options={clientOptions.map(c => ({key: c.id, text: c.name}))}
                  selectedKey={model.client_id || undefined}
                  // text={selectedClientText}
                  // onResolveOptions={onClientResolve}
                  onChange={(_, option) => {
                    if (option) {
                      if (!clientOptions.some((o) => o.key === option.key)) {
                        setClientOptions((prev) => [...prev, option]);
                      }
                      setField('client_id', option.key);
                    }
                  }}
                  // styles={{ root: { width: 360 } }}
                />
              </Stack.Item>
              <Stack.Item grow={1}>
                <DatePicker
                  label="Date Submitted"
                  value={ymdToDate(model.date_submitted)}
                  // onChange={(e)}
                  onSelectDate={(date) => console.log(date)}
                  // onSelectDate={(d) => setField('date_submitted', dateToYMD(d))}
                />
              </Stack.Item>
              <Stack.Item grow={1}>
                <Dropdown
                  label="Status"
                  options={[
                    { key: 'draft', text: 'Draft' },
                    { key: 'submitted', text: 'Submitted' },
                    { key: 'awarded', text: 'Awarded' },
                    { key: 'lost', text: 'Lost' },
                    { key: 'on-hold', text: 'On Hold' },
                  ]}
                  selectedKey={model.status || undefined}
                  onChange={(_, opt) => setField('status', opt?.key || '')}
                  // styles={{ root: { width: 200 } }}
                />
              </Stack.Item>
            </Stack>

            <Stack horizontal tokens={{ childrenGap: 16 }}>
              <Stack.Item grow={1}>
                <TextField
                  label="Proposal Type"
                  value={model.proposal_type}
                  onChange={(_, v) => setField('proposal_type', v || '')}
                  // styles={{ root: { width: 260} }}
                />
              </Stack.Item>
              <Stack.Item grow={1}>
                <TextField
                  label="Year"
                  type="number"
                  value={model.year}
                  onChange={(_, v) => setField('year', v || '')}
                  // styles={{ root: { width: 140} }}
                />
              </Stack.Item>
              <Stack.Item grow={1}>
                <Toggle
                  label="Was Successful"
                  checked={!!model.was_successful}
                  onChange={(_, c) => setField('was_successful', !!c)}
                />
              </Stack.Item>
            </Stack>


          </Stack>
        </PivotItem>

        {/* -------- Narrative tab -------- */}
        <PivotItem headerText="Narrative">
          <Stack tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: 12 } }}>
            <Stack.Item className={styles.sectionCard}>
              <Text variant="xLarge">About</Text>
              <Separator />
              <TextField
                value={model.about}
                onChange={(_, v) => setField('about', v || '')}
                multiline
                autoAdjustHeight
              />
            </Stack.Item>
            <Stack.Item className={styles.sectionCard}>
              <Text variant='xLarge'>Approach</Text>
              <Separator />
              <TextField
                value={model.approach}
                onChange={(_, v) => setField('approach', v || '')}
                autoAdjustHeight
                multiline
              />
            </Stack.Item>

            <Stack.Item className={styles.sectionCard}>
              <Text variant='xLarge'>Workplan</Text>
              <TextField
                value={model.workplan}
                onChange={(_, v) => setField('workplan', v || '')}
                multiline
                autoAdjustHeight
              />
            </Stack.Item>

            <Stack.Item className={styles.sectionCard}>
              <Text variant='xLarge'>Budget</Text>
              <Separator />
              <TextField
                value={model.budget}
                onChange={(_, v) => setField('budget', v || '')}
                multiline 
                autoAdjustHeight
              />
            </Stack.Item>
            
            <Stack.Item className={styles.sectionCard}>
              <Text variant='xLarge'>Search Text</Text>
              <Separator />
              <TextField
                label="Search Text"
                value={model.search_text}
                onChange={(_, v) => setField('search_text', v || '')}
                autoAdjustHeight
                multiline
              />
            </Stack.Item>

          </Stack>
        </PivotItem>

        {/* -------- Team & Bios tab -------- */}
        <PivotItem headerText="Team & Bios">
          <Stack tokens={{ childrenGap: 16 }} styles={{ root: { marginTop: 12 } }}>
            {/* People manager to add/remove team members on the proposal */}
            <Stack className={styles.sectionCard}>
              <PeopleManager entityType="proposals" entityId={proposalId} />
            </Stack>


            <Stack className={styles.sectionCard}>
              <ProposalBioVariationsEditor proposalId={proposalId} />
            </Stack>
          </Stack>
        </PivotItem>

        {/* -------- Referenced Projects --------- */}
        <PivotItem headerText='Linked Projects'>
          <Stack styles={{root: {marginTop: 12}}} className={styles.sectionCard}>
            <ProjectLinkManager entityId={proposalId} entityType={'proposal'} />
          </Stack>
        </PivotItem>

        {/* -------- Tags tab -------- */}
        <PivotItem headerText="Tags">
          <Stack styles={{root: {marginTop: 12}}} className={styles.sectionCard}>
            <TagManager entityType="proposals" entityId={proposalId} />
          </Stack>
        </PivotItem>
      </Pivot>
    </Stack>
  );
}
